import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { DatePickerModule } from 'primeng/datepicker';
import { ActivatedRoute } from '@angular/router';

import { ApiService } from '../../services/api.service';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../shared/constants/pagination.constants';

@Component({
    selector: 'app-follow-up-list',
    imports: [CommonModule, FormsModule, FontAwesomeModule, DatePickerModule],
    templateUrl: './follow-up-list.component.html',
    styleUrls: ['../lead-management.shared.css']
})
export class FollowUpListComponent implements OnInit {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  followUps: any[] = [];
  loading = false;
  currentPage = 1;
  pageSize = 10;
  totalRecords = 0;
  totalPages = 1;
  nextPage: string | null = null;
  previousPage: string | null = null;
  selectedFollowUp: any = null;
  showEditModal = false;

  followUpForm: any = {
    follow_up_type: 'CALL',
    follow_up_date: '',
    notes: '',
    next_follow_up_date: '',
  };

  filters: any = {
    search: '',
    date: null,
  };

  followUpTypes = [
    ['CALL', 'Call'],
    ['WHATSAPP', 'WhatsApp'],
    ['EMAIL', 'Email'],
    ['MEETING', 'Meeting'],
    ['DEMO', 'Demo'],
    ['OTHER', 'Other'],
  ];

  constructor(
    private apiService: ApiService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    library: FaIconLibrary,
  ) {
    library.addIcons(faPen, faTrash);
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      this.filters = {
        search: params.get('search') || '',
        date: this.parseDate(params.get('date')),
      };
      this.currentPage = Number(params.get('page') || 1);
      this.loadFollowUps();
    });
  }

  loadFollowUps(): void {
    this.loading = true;
    this.apiService.listLeadFollowUps(this.currentPage, this.pageSize, this.filters).subscribe({
      next: (response: any) => {
        this.followUps = response?.results || [];
        this.totalRecords = response?.count || 0;
        this.totalPages = Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
        this.nextPage = response?.next || null;
        this.previousPage = response?.previous || null;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadFollowUps();
  }

  resetFilters(): void {
    this.filters = {
      search: '',
      date: null,
    };
    this.currentPage = 1;
    this.loadFollowUps();
  }

  changePage(step: number): void {
    const nextPage = this.currentPage + step;
    if (nextPage < 1 || nextPage > this.totalPages) {
      return;
    }
    this.currentPage = nextPage;
    this.loadFollowUps();
  }

  next(): void {
    if (!this.nextPage) {
      return;
    }
    this.currentPage++;
    this.loadFollowUps();
  }

  previous(): void {
    if (!this.previousPage) {
      return;
    }
    this.currentPage--;
    this.loadFollowUps();
  }

  selectPageSize(): void {
    this.currentPage = 1;
    this.loadFollowUps();
  }

  openEdit(followUp: any): void {
    this.selectedFollowUp = followUp;
    this.followUpForm = {
      follow_up_type: followUp.follow_up_type,
      follow_up_date: this.parseDate(followUp.follow_up_date),
      notes: followUp.notes || '',
      next_follow_up_date: this.parseDate(followUp.next_follow_up_date),
    };
    this.showEditModal = true;
  }

  saveFollowUp(): void {
    if (!this.selectedFollowUp?.id || !this.selectedFollowUp?.lead) {
      return;
    }
    this.apiService.updateLeadFollowUp(
      this.selectedFollowUp.lead,
      this.selectedFollowUp.id,
      this.serializeFollowUpForm(this.followUpForm),
    ).subscribe({
      next: () => {
        this.toastr.success('Follow-up updated successfully.', 'Success');
        this.showEditModal = false;
        this.loadFollowUps();
      },
      error: (err) => {
        const message = err?.error?.detail || 'Unable to update follow-up.';
        this.toastr.error(message, 'Error');
      }
    });
  }

  deleteFollowUp(followUp: any): void {
    if (!followUp?.id || !followUp?.lead || !window.confirm(`Delete follow-up for ${followUp.customer_name}?`)) {
      return;
    }
    this.apiService.deleteLeadFollowUp(followUp.lead, followUp.id).subscribe({
      next: () => {
        this.toastr.success('Follow-up deleted successfully.', 'Success');
        if (this.followUps.length === 1 && this.currentPage > 1) {
          this.currentPage--;
        }
        this.loadFollowUps();
      },
      error: (err) => {
        const message = err?.error?.detail || 'Unable to delete follow-up.';
        this.toastr.error(message, 'Error');
      }
    });
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private serializeFollowUpForm(form: any): any {
    return {
      ...form,
      follow_up_date: form.follow_up_date ? formatDate(form.follow_up_date, 'yyyy-MM-dd', 'en-IN') : null,
      next_follow_up_date: form.next_follow_up_date ? formatDate(form.next_follow_up_date, 'yyyy-MM-dd', 'en-IN') : null,
    };
  }
}
